import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ProductForm = ({ product, onSave, onCancel, isEditing }) => {
  const [activeTab, setActiveTab] = useState('basic');

  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm({
    defaultValues: {
      sku: product?.sku || '',
      name: product?.name || '',
      category: product?.category || '',
      brand: product?.brand || '',
      model: product?.model || '',
      description: product?.description || '',
      price: product?.price || 0,
      cost: product?.cost || 0,
      minimumStock: product?.minimumStock || 5,
      maximumStock: product?.maximumStock || 100,
      location: product?.location || '',
      barcode: product?.barcode || '',
      warranty: product?.warranty || 12,
      weight: product?.specifications?.weight || '',
      dimensions: product?.specifications?.dimensions || '',
      color: product?.specifications?.color || '',
      material: product?.specifications?.material || '',
      supplierName: product?.supplier?.name || '',
      supplierContact: product?.supplier?.contact || '',
      supplierLeadTime: product?.supplier?.leadTime || 7,
      tags: product?.tags?.join(', ') || '',
      status: product?.status || 'active'
    }
  });

  const categories = [
    'Portátiles', 'Smartphones', 'Tablets', 'Monitores', 'Accesorios',
    'Audio', 'Gaming', 'Redes', 'Almacenamiento', 'Impresoras'
  ];

  const generateSKU = () => {
    const brand = watch('brand');
    const category = watch('category');
    const timestamp = Date.now().toString().slice(-4);
    
    if (brand && category) {
      const brandCode = brand.substring(0, 3).toUpperCase();
      const categoryCode = category.substring(0, 3).toUpperCase();
      setValue('sku', `${brandCode}-${categoryCode}-${timestamp}`);
    }
  };

  const onSubmit = (data) => {
    const productData = {
      ...data,
      specifications: {
        weight: data.weight,
        dimensions: data.dimensions,
        color: data.color,
        material: data.material
      },
      supplier: {
        id: product?.supplier?.id || Date.now(),
        name: data.supplierName,
        contact: data.supplierContact,
        leadTime: parseInt(data.supplierLeadTime)
      },
      tags: data.tags ? data.tags.split(',').map(tag => tag.trim()).filter(Boolean) : [],
      price: parseFloat(data.price),
      cost: parseFloat(data.cost),
      minimumStock: parseInt(data.minimumStock),
      maximumStock: parseInt(data.maximumStock),
      warranty: parseInt(data.warranty)
    };

    onSave(productData);
  };

  const tabs = [
    { id: 'basic', label: 'Información Básica', icon: 'Info' },
    { id: 'pricing', label: 'Precios y Stock', icon: 'DollarSign' },
    { id: 'details', label: 'Detalles Técnicos', icon: 'Settings' },
    { id: 'supplier', label: 'Proveedor', icon: 'Truck' }
  ];

  return (
    <div className="bg-surface rounded-lg border border-border">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-text-primary">
            {isEditing ? 'Editar Producto' : 'Nuevo Producto'}
          </h2>
          <div className="flex space-x-2">
            <Button
              variant="secondary"
              onClick={onCancel}
              iconName="X"
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit(onSubmit)}
              iconName="Save"
            >
              Guardar
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-border">
        <nav className="flex space-x-0">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-primary text-primary' :'border-transparent text-text-secondary hover:text-text-primary'
              }`}
            >
              <Icon name={tab.icon} size={16} />
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      <div className="p-6">
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Basic Information Tab */}
          {activeTab === 'basic' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    SKU *
                  </label>
                  <div className="flex space-x-2">
                    <Input
                      {...register('sku', { required: 'El SKU es obligatorio' })}
                      placeholder="SKU del producto"
                      error={errors.sku?.message}
                    />
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={generateSKU}
                      iconName="RefreshCw"
                      size="sm"
                    >
                      Generar
                    </Button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Nombre del Producto *
                  </label>
                  <Input
                    {...register('name', { required: 'El nombre es obligatorio' })}
                    placeholder="Nombre del producto"
                    error={errors.name?.message}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Categoría *
                  </label>
                  <select
                    {...register('category', { required: 'La categoría es obligatoria' })}
                    className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="">Seleccionar categoría...</option>
                    {categories.map(category => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                  {errors.category && (
                    <p className="mt-1 text-sm text-error">{errors.category.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Marca
                  </label>
                  <Input
                    {...register('brand')}
                    placeholder="Marca del producto"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Modelo
                  </label>
                  <Input
                    {...register('model')}
                    placeholder="Modelo del producto"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Código de Barras
                  </label>
                  <Input
                    {...register('barcode')}
                    placeholder="Código de barras"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Descripción
                </label>
                <textarea
                  {...register('description')}
                  rows={4}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                  placeholder="Descripción detallada del producto"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Etiquetas
                </label>
                <Input
                  {...register('tags')}
                  placeholder="laptop, ultrabook, premium (separadas por comas)"
                />
                <p className="mt-1 text-xs text-text-muted">
                  Separe las etiquetas con comas
                </p>
              </div>
            </div>
          )}

          {/* Pricing and Stock Tab */}
          {activeTab === 'pricing' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Precio de Venta (€) *
                  </label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    {...register('price', { 
                      required: 'El precio es obligatorio',
                      min: { value: 0, message: 'El precio debe ser positivo' }
                    })}
                    placeholder="0.00"
                    error={errors.price?.message}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Precio de Coste (€) *
                  </label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    {...register('cost', { 
                      required: 'El coste es obligatorio',
                      min: { value: 0, message: 'El coste debe ser positivo' }
                    })}
                    placeholder="0.00"
                    error={errors.cost?.message}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Stock Mínimo *
                  </label>
                  <Input
                    type="number"
                    min="0"
                    {...register('minimumStock', { 
                      required: 'El stock mínimo es obligatorio',
                      min: { value: 0, message: 'Debe ser un número positivo' }
                    })}
                    placeholder="5"
                    error={errors.minimumStock?.message}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Stock Máximo *
                  </label>
                  <Input
                    type="number"
                    min="1"
                    {...register('maximumStock', { 
                      required: 'El stock máximo es obligatorio',
                      min: { value: 1, message: 'Debe ser mayor a 0' }
                    })}
                    placeholder="100"
                    error={errors.maximumStock?.message}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Ubicación en Almacén
                  </label>
                  <Input
                    {...register('location')}
                    placeholder="A1-01"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Garantía (meses)
                  </label>
                  <Input
                    type="number"
                    min="0"
                    {...register('warranty')}
                    placeholder="12"
                  />
                </div>
              </div>

              {/* Margin Calculation */}
              <div className="bg-secondary-50 p-4 rounded-lg">
                <h4 className="font-medium text-text-primary mb-2">Cálculo de Margen</h4>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-text-secondary">Precio Venta:</span>
                    <p className="font-medium">€{watch('price') || 0}</p>
                  </div>
                  <div>
                    <span className="text-text-secondary">Precio Coste:</span>
                    <p className="font-medium">€{watch('cost') || 0}</p>
                  </div>
                  <div>
                    <span className="text-text-secondary">Margen:</span>
                    <p className="font-medium">
                      €{((watch('price') || 0) - (watch('cost') || 0)).toFixed(2)} 
                      ({watch('price') && watch('cost') 
                        ? (((watch('price') - watch('cost')) / watch('price')) * 100).toFixed(1) 
                        : 0}%)
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Technical Details Tab */}
          {activeTab === 'details' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Peso
                  </label>
                  <Input
                    {...register('weight')}
                    placeholder="1.2 kg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Dimensiones
                  </label>
                  <Input
                    {...register('dimensions')}
                    placeholder="295.7 x 198.1 x 14.8 mm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Color
                  </label>
                  <Input
                    {...register('color')}
                    placeholder="Plata"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Material
                  </label>
                  <Input
                    {...register('material')}
                    placeholder="Aluminio"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Estado del Producto
                </label>
                <select
                  {...register('status')}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="active">Activo</option>
                  <option value="inactive">Inactivo</option>
                  <option value="discontinued">Descontinuado</option>
                </select>
              </div>
            </div>
          )}

          {/* Supplier Tab */}
          {activeTab === 'supplier' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Nombre del Proveedor
                  </label>
                  <Input
                    {...register('supplierName')}
                    placeholder="Dell Technologies"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Contacto del Proveedor
                  </label>
                  <Input
                    {...register('supplierContact')}
                    placeholder="ventas@dell.es"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Tiempo de Entrega (días)
                  </label>
                  <Input
                    type="number"
                    min="1"
                    {...register('supplierLeadTime')}
                    placeholder="7"
                  />
                </div>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default ProductForm;